import { sanitizeMaoForPaf } from "@/components/PAFDialog/pafPricing";
import { PAF_RATE } from "@/constants/maoCalculator";

/** Default buying-agent commission percent used for Seller Net Offer on new investor offers. */
export const OFFER_BUYING_AGENT_COMM_RATE = 2;

/** Max buying-agent commission percent allowed in the offer wizard. */
export const OFFER_BUYING_AGENT_COMM_RATE_MAX = 99;

/**
 * Normalizes a buying-agent commission percent for Seller Net Offer math.
 *
 * @param {*} value - Raw percent from UI
 * @param {number} [fallback=OFFER_BUYING_AGENT_COMM_RATE]
 * @returns {number} Non-negative percent capped at {@link OFFER_BUYING_AGENT_COMM_RATE_MAX}
 */
export function normalizeBuyingAgentCommRate(value, fallback = OFFER_BUYING_AGENT_COMM_RATE) {
  if (value == null || value === "") return fallback;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return Math.min(OFFER_BUYING_AGENT_COMM_RATE_MAX, parsed);
}

/**
 * Resolves the investor-submitted offer amount, preferring offer_amount over legacy mao.
 *
 * @param {object|null|undefined} calculation - Deal calculation payload
 * @returns {number} Positive offer amount, or 0 when unavailable
 */
export function resolveOfferAmount(calculation) {
  const offerAmount = Number(calculation?.offer_amount);
  if (Number.isFinite(offerAmount) && offerAmount > 0) {
    return offerAmount;
  }
  const mao = Number(calculation?.mao);
  if (Number.isFinite(mao) && mao > 0) {
    return mao;
  }
  return 0;
}

/**
 * Computes PAF (1%), buying-agent commission, and Seller Net Offer from an investor offer.
 *
 * @param {*} offerAmount - Investor-entered offer amount
 * @param {number|string|null|undefined} [buyingAgentRate=OFFER_BUYING_AGENT_COMM_RATE] - Commission percent
 * @returns {{
 *   offerAmount: number,
 *   pafAmount: number,
 *   buyingAgentRate: number,
 *   buyingAgentComm: number,
 *   sellerNetOffer: number,
 * } | null}
 */
export function computeSellerNetOfferBreakdown(
  offerAmount,
  buyingAgentRate = OFFER_BUYING_AGENT_COMM_RATE
) {
  const normalized = normalizeSubmittedOfferAmount(offerAmount);
  if (normalized == null) return null;

  const rate = normalizeBuyingAgentCommRate(buyingAgentRate, OFFER_BUYING_AGENT_COMM_RATE);
  const pafAmount = Math.round((normalized * PAF_RATE) / 100);
  const buyingAgentComm = Math.round((normalized * rate) / 100);
  const sellerNetOffer = normalized - pafAmount - buyingAgentComm;
  // Allow $0 Seller Net (e.g. 99% commission + 1% PAF) so the breakdown still renders.
  if (sellerNetOffer < 0) return null;

  return {
    offerAmount: normalized,
    pafAmount,
    buyingAgentRate: rate,
    buyingAgentComm,
    sellerNetOffer,
  };
}

/**
 * Converts a realtor-entered Seller Net Offer back to the investor-facing gross amount.
 *
 * @param {*} sellerNetOffer
 * @param {number|string|null|undefined} [buyingAgentRate=OFFER_BUYING_AGENT_COMM_RATE]
 * @returns {number|null}
 */
export function computeInvestorOfferAmount(
  sellerNetOffer,
  buyingAgentRate = OFFER_BUYING_AGENT_COMM_RATE
) {
  const normalizedNet = normalizeSubmittedOfferAmount(sellerNetOffer);
  if (normalizedNet == null) return null;

  const rate = normalizeBuyingAgentCommRate(buyingAgentRate, OFFER_BUYING_AGENT_COMM_RATE);
  const netRate = 1 - (PAF_RATE + rate) / 100;
  if (netRate <= 0) return null;

  return normalizeSubmittedOfferAmount(normalizedNet / netRate);
}

/**
 * Resolves the offer amount to show for the current viewer.
 * Investors see their original entered amount when present; otherwise everyone sees calculation.offer_amount
 * (Seller Net for realtor-facing new offers; counters are stored as-entered with no translation).
 *
 * @param {object|null|undefined} deal
 * @param {{ isInvestor?: boolean }} [context]
 * @returns {number}
 */
export function resolveViewerOfferAmount(deal, context = {}) {
  if (context.isInvestor) {
    const investorOfferAmount = normalizeSubmittedOfferAmount(
      deal?.metadata?.investor_offer_amount
    );
    if (investorOfferAmount != null) {
      return investorOfferAmount;
    }
  }
  return resolveOfferAmount(deal?.calculation);
}

/**
 * Short label for the basis used by PAF + buying agent (Offer when set, else MAO).
 *
 * @param {object|null|undefined} calculation - Deal calculation payload
 * @returns {"Offer"|"MAO"}
 */
export function resolvePurchaseBasisLabel(calculation) {
  const offerAmount = Number(calculation?.offer_amount);
  if (Number.isFinite(offerAmount) && offerAmount > 0) {
    return "Offer";
  }
  return "MAO";
}

/**
 * True when calculation includes an explicit zero offer_amount (distinct from missing/invalid).
 *
 * @param {object|null|undefined} calculation - Deal calculation payload
 * @returns {boolean}
 */
export function hasExplicitZeroOfferAmount(calculation) {
  if (!calculation || typeof calculation !== "object") {
    return false;
  }
  if (!Object.prototype.hasOwnProperty.call(calculation, "offer_amount")) {
    return false;
  }
  const offerAmount = Number(calculation.offer_amount);
  return Number.isFinite(offerAmount) && offerAmount === 0;
}

/**
 * Normalizes a submitted offer amount: finite, positive, whole dollars, PAF-bounded.
 *
 * @param {*} value - Raw offer amount from UI or API
 * @returns {number|null} Normalized amount, or null when invalid
 */
export function normalizeSubmittedOfferAmount(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  const rounded = Math.round(parsed);
  if (rounded <= 0) {
    return null;
  }
  const sanitized = sanitizeMaoForPaf(rounded);
  if (sanitized <= 0) {
    return null;
  }
  return sanitized;
}

/**
 * Limits fractional digits in a numeric draft string.
 *
 * @param {string} draft - Sanitized numeric draft
 * @param {number} [maxPlaces=2] - Maximum decimal places
 * @returns {string}
 */
export function clampDecimalPlaces(draft, maxPlaces = 2) {
  if (!draft || maxPlaces < 0) {
    return draft ?? "";
  }
  const dotIndex = draft.indexOf(".");
  if (dotIndex === -1) {
    return draft;
  }
  const intPart = draft.slice(0, dotIndex + 1);
  const decPart = draft.slice(dotIndex + 1).slice(0, maxPlaces);
  return intPart + decPart;
}
